import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Plus, FileText, Lock, ShoppingBag, Sparkles, History, Crown, Cake } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, errMsg, fmtDate, fmtPrice, mediaUrl } from "@/lib/api";
import { PageTitle, Panel, Label } from "@/pages/admin/AdminLayout";
import { REQ_TYPES, REQ_STATUS, NewRequestModal, clientWa } from "@/pages/admin/Requests";
import { HoldRow } from "@/pages/admin/Stock";

export const VipBadge = ({ testId }) => <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#0C0B09] bg-[#C8AE6E] px-2 py-0.5 align-middle" data-testid={testId}><Crown className="w-3 h-3" /> VIP</span>;
const bdayLabel = (b) => (b ? new Date(`${b}T12:00:00`).toLocaleDateString("pt-PT", { day: "2-digit", month: "long" }) : "—");
const isBdaySoon = (b) => {
  if (!b) return false;
  const t = new Date(); const d = new Date(t.getFullYear(), Number(b.slice(5, 7)) - 1, Number(b.slice(8, 10)));
  if (d < new Date(t.getFullYear(), t.getMonth(), t.getDate())) d.setFullYear(t.getFullYear() + 1);
  return (d - t) / 864e5 <= 7;
};

export function Clients() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/clients", { params: { q: q || undefined } }).then((r) => setItems(r.data)), 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div data-testid="admin-clients">
      <PageTitle title="Clientes" sub={items ? `${items.length} clientes · agrupados pelo telefone / WhatsApp` : ""} />
      <input className="afield mb-5" placeholder="Pesquisar por nome ou telefone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" />
      <div className="overflow-x-auto border border-[#282520]">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="bg-[#161512] text-[10px] uppercase tracking-[0.2em] text-[#A39E93]"><tr>{["Cliente", "Telefone", "Aniversário", "Pedidos", "Em aberto", "Reservas", "Compras", "Total comprado", "Última atividade"].map((h) => <th key={h} className="text-left font-normal px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {(items || []).map((c) => (
              <tr key={c.key} className="border-t border-[#282520] hover:bg-white/[0.02]">
                <td className="px-4 py-3"><Link to={`/admin/clientes/${c.key}`} className="text-[#E6D5AC] hover:underline" data-testid={`client-open-${c.key}`}>{c.name}</Link>{c.vip && <span className="ml-2"><VipBadge testId={`client-vip-${c.key}`} /></span>}</td>
                <td className="px-4 py-3 text-[#A39E93] whitespace-nowrap">{c.phone}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${isBdaySoon(c.birthday) ? "text-[#C8AE6E]" : "text-[#A39E93]"}`} data-testid={`client-birthday-${c.key}`}>{isBdaySoon(c.birthday) && <Cake className="w-3.5 h-3.5 inline mr-1" />}{bdayLabel(c.birthday)}</td>
                <td className="px-4 py-3">{c.requests}</td>
                <td className="px-4 py-3">{c.open}</td>
                <td className="px-4 py-3">{c.reserved ? <span className="text-[#C8AE6E]">{c.reserved}</span> : 0}</td>
                <td className="px-4 py-3">{c.purchases}</td>
                <td className="px-4 py-3 whitespace-nowrap">{c.spent_cents ? fmtPrice(c.spent_cents) : "—"}</td>
                <td className="px-4 py-3 text-[#A39E93] whitespace-nowrap">{fmtDate(c.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items && items.length === 0 && <p className="p-8 text-center text-sm text-[#A39E93]">Sem clientes.</p>}
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <Panel data-testid={testId}>
    <h3 className="text-lg mb-4 flex items-center gap-2"><Icon className="w-4 h-4 text-[#C8AE6E]" strokeWidth={1.4} /> {title} <span className="text-xs text-[#A39E93]">({count})</span></h3>
    {count === 0 ? <p className="text-xs text-[#A39E93]">Nada a mostrar.</p> : children}
  </Panel>
);

const reqLabel = (r) => r.watch_label || [r.specific?.brand, r.specific?.model].filter(Boolean).join(" ") || "—";

function Reserved({ c, reload }) {
  return (
    <Section icon={Lock} title="Em reserva (stock cativo)" count={c.reserved.length} testId="client-reserved">
      <div className="space-y-3">{c.reserved.map((h) => (
        <div key={h.id} className="space-y-2">
          <div className="flex items-center gap-3">
            {h.photo && <img src={mediaUrl(h.photo)} alt="" className="w-10 h-12 object-cover" />}
            <Link to={`/admin/relogios/${h.watch_id}`} className="text-sm hover:text-[#E6D5AC]">{h.watch_label} <span className="text-xs text-[#A39E93]">{h.reference}</span></Link>
          </div>
          <HoldRow wid={h.watch_id} h={h} onChange={reload} listPrice={h.list_price_cents} />
        </div>
      ))}</div>
    </Section>
  );
}

function Purchases({ c }) {
  return (
    <Section icon={ShoppingBag} title="Comprados" count={c.purchases.length} testId="client-purchases">
      <div className="divide-y divide-[#282520]">{c.purchases.map((s) => (
        <div key={s.id} className="py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`client-sale-${s.id}`}>
          <Link to={`/admin/relogios/${s.watch_id}`} className="flex-1 min-w-[180px] hover:text-[#E6D5AC]">{s.brand} {s.model}<span className="block text-xs text-[#A39E93]">{s.ref} · sn {s.serial || "—"} · {s.sale_date}</span></Link>
          <span className="text-[#E6D5AC]">{s.price_cents ? fmtPrice(s.price_cents) : "—"}</span>
          <a href={`${BACKEND}/api/admin/sales/${s.id}/delivery.pdf?lang=pt`} target="_blank" rel="noopener noreferrer" className="btn-line !py-1 !px-3 text-xs" data-testid={`client-sale-pdf-${s.id}`}><FileText className="w-3.5 h-3.5" /> Garantia</a>
        </div>
      ))}</div>
    </Section>
  );
}

function Interests({ c }) {
  return (
    <Section icon={Sparkles} title="Com interesse" count={c.interests.length} testId="client-interests">
      <div className="divide-y divide-[#282520]">{c.interests.map((r) => (
        <Link key={r.id} to={`/admin/pedidos/${r.id}`} className="py-2.5 flex flex-wrap gap-3 text-sm hover:text-[#E6D5AC]" data-testid={`client-interest-${r.id}`}>
          <span className="flex-1 min-w-[180px]">{reqLabel(r)}<span className="block text-xs text-[#A39E93]">{r.ref} · {REQ_TYPES[r.type]}{r.visit_date ? ` · visita ${r.visit_date}` : ""}</span></span>
          <span className="text-xs text-[#A39E93]">{REQ_STATUS[r.status]}</span>
        </Link>
      ))}</div>
    </Section>
  );
}

const EV = { request: "Pedido", status: "Estado", note: "Nota no pedido", hold: "Reserva", deposit: "Sinal", release: "Libertada", sale: "Venda", client_note: "Nota do cliente" };

function Timeline({ c }) {
  return (
    <Section icon={History} title="Histórico" count={c.timeline.length} testId="client-timeline">
      <div className="space-y-3 max-h-[520px] overflow-y-auto pr-2">{c.timeline.map((e, i) => (
        <div key={i} className="text-sm border-l border-[#C8AE6E]/40 pl-3">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#C8AE6E]">{EV[e.kind]}{e.ref ? ` · ${e.ref}` : ""}</p>
          <p className="whitespace-pre-wrap">{e.kind === "status" ? `→ ${REQ_STATUS[e.status] || e.status}` : e.kind === "request" ? `${REQ_TYPES[e.type] || e.type} · ${e.text || "—"}` : e.text}{e.cents ? ` · ${fmtPrice(e.cents)}` : ""}</p>
          <p className="text-[11px] text-[#A39E93]">{fmtDate(e.at)}{e.author ? ` · ${e.author}` : ""}{e.request_id ? <> · <Link to={`/admin/pedidos/${e.request_id}`} className="underline">abrir pedido</Link></> : null}</p>
        </div>
      ))}</div>
    </Section>
  );
}

function ClientProfile({ c, onSaved }) {
  const [b, setB] = useState(c.birthday || "");
  const [w, setW] = useState(c.wrist_cm ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put(`/admin/clients/${c.key}/profile`, { birthday: b || null, wrist_cm: w === "" ? null : Number(String(w).replace(",", ".")) }); toast.success("Perfil do cliente guardado"); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Panel>
      <h3 className="text-lg mb-3 flex items-center gap-2"><Cake className="w-4 h-4 text-[#C8AE6E]" strokeWidth={1.4} /> Perfil</h3>
      <form onSubmit={save} className="grid grid-cols-2 gap-3 items-end">
        <label className="block"><Label>Aniversário</Label><input type="date" className="afield [color-scheme:dark]" value={b} onChange={(e) => setB(e.target.value)} data-testid="client-birthday-input" /></label>
        <label className="block"><Label>Pulso (cm)</Label><input className="afield" inputMode="decimal" placeholder="ex. 17,5" value={w} onChange={(e) => setW(e.target.value)} data-testid="client-wrist-input" /></label>
        <p className="col-span-2 text-[11px] text-[#A39E93]">Lembrete no Telegram às 9h no dia e 7 dias antes (evento "Aniversário de cliente").</p>
        <button className="btn-line !py-2 text-xs col-span-2" disabled={busy} data-testid="client-profile-save">{busy ? "A guardar…" : "Guardar perfil"}</button>
      </form>
    </Panel>
  );
}

function ClientNotes({ c, onAdd }) {
  const [t, setT] = useState("");
  const add = async (e) => { e.preventDefault(); try { await api.post(`/admin/clients/${c.key}/notes`, { text: t }); setT(""); toast.success("Nota adicionada"); onAdd(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Panel>
      <h3 className="text-lg mb-3">Notas do cliente</h3>
      <div className="space-y-3 mb-4" data-testid="client-notes">{c.notes.map((n) => <div key={n.id} className="text-sm border-l border-[#C8AE6E]/40 pl-3"><p className="whitespace-pre-wrap">{n.text}</p><p className="text-[11px] text-[#A39E93] mt-1">{n.author} · {fmtDate(n.at)}</p></div>)}</div>
      <form onSubmit={add} className="space-y-2"><textarea className="afield min-h-[70px]" value={t} onChange={(e) => setT(e.target.value)} placeholder="Preferências, tamanho do pulso, ocasiões…" data-testid="client-note-input" /><button className="btn-line !py-2 text-xs" disabled={!t.trim()} data-testid="client-note-submit">Adicionar nota</button></form>
    </Panel>
  );
}

export function ClientDetail() {
  const { key } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const load = useCallback(() => api.get(`/admin/clients/${key}`).then((r) => setC(r.data)).catch(() => setC(false)), [key]);
  useEffect(() => { load(); }, [load]);
  if (c === false) return <p className="text-sm text-[#A39E93]">Cliente não encontrado.</p>;
  if (!c) return null;
  const spent = c.purchases.reduce((a, s) => a + (s.price_cents || 0), 0);
  return (
    <div data-testid="admin-client-detail">
      <Link to="/admin/clientes" className="text-xs text-[#A39E93] hover:text-[#E6D5AC] flex items-center gap-1 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Clientes</Link>
      <PageTitle title={<>{c.name}{c.vip && <span className="ml-3"><VipBadge testId="client-detail-vip" /></span>}</>} sub={`${c.phone} · cliente desde ${fmtDate(c.first_at)} · idioma ${(c.lang || "pt").toUpperCase()}${c.birthday ? ` · aniversário ${bdayLabel(c.birthday)}` : ""}${c.wrist_cm ? ` · pulso ${String(c.wrist_cm).replace(".", ",")} cm` : ""}`}>
        <a href={clientWa(c, `Olá ${c.name.split(" ")[0]}, daqui fala a GoldenTime.`)} target="_blank" rel="noopener noreferrer" className="btn-line !py-2.5" data-testid="client-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        <button className="btn-gold !py-2.5" onClick={() => setNewOpen(true)} data-testid="client-new-request"><Plus className="w-4 h-4" /> Novo pedido</button>
      </PageTitle>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[["Pedidos", c.requests.length, "requests"], ["Em reserva", c.reserved.length, "reserved"], ["Compras", c.purchases.length, "purchases"], ["Total comprado", spent ? fmtPrice(spent) : "—", "spent"]].map(([l, v, k]) => (
          <Panel key={k} className="!p-4"><Label>{l}</Label><span className="font-display text-2xl text-[#E6D5AC]" data-testid={`client-kpi-${k}`}>{v}</span></Panel>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
        <div className="space-y-6">
          <Reserved c={c} reload={load} />
          <Purchases c={c} />
          <Interests c={c} />
          <Section icon={FileText} title="Todos os pedidos" count={c.requests.length} testId="client-requests">
            <div className="divide-y divide-[#282520]">{c.requests.map((r) => (
              <Link key={r.id} to={`/admin/pedidos/${r.id}`} className="py-2.5 flex flex-wrap gap-3 text-sm hover:text-[#E6D5AC]" data-testid={`client-request-${r.id}`}>
                <span className="text-xs text-[#E6D5AC] w-24">{r.ref}</span>
                <span className="flex-1 min-w-[160px]">{REQ_TYPES[r.type]} · {reqLabel(r)}</span>
                <span className="text-xs text-[#A39E93]">{REQ_STATUS[r.status]} · {fmtDate(r.created_at)}</span>
              </Link>
            ))}</div>
          </Section>
        </div>
        <div className="space-y-6">
          <ClientProfile key={`${c.birthday}-${c.wrist_cm}`} c={c} onSaved={load} />
          <ClientNotes c={c} onAdd={load} />
          <Timeline c={c} />
        </div>
      </div>
      {newOpen && <NewRequestModal initial={{ name: c.name, phone: c.phone, salutation: c.salutation || "", lang: c.lang || "pt" }} onClose={() => setNewOpen(false)} onCreated={(d) => { setNewOpen(false); nav(`/admin/pedidos/${d.id}`); }} />}
    </div>
  );
}
